import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Download, Loader2, Play, Plus, Save, Trash2, UserRound } from 'lucide-react';

import { ttsConfigAPI } from '../../services/api';
import { Alert, AlertDescription } from '../ui/alert';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from '../ui/use-toast';

// 2026-09-30 (Docs/VOICE_CATALOG_CALLER_VOICE_CHOICE_PLAN.md): the voices a
// caller (on a WhatsApp call) or a WhatsApp chat user can choose from —
// e.g. "Amani, Kenyan male, calm" — with one on/off switch per channel. A
// contact's pick is saved on the contact and used on every later call and
// WhatsApp voice note. The Digital Companion keeps its own voice. Backend:
// app/api/tts_config.py (/voice-selection, /elevenlabs-voices,
// /voice-sample), app/services/voice_selection_service.py.

type VoiceEntry = {
  key: string;
  label: string;
  provider: 'elevenlabs' | 'edge_tts';
  voice_id: string;
  gender: string;
  accent: string;
  languages: string[];
  style: string;
  description: string;
  sample_text: Record<string, string>;
  enabled: boolean;
};

type VoiceSelection = {
  calls_enabled: boolean;
  whatsapp_enabled: boolean;
  voices: VoiceEntry[];
};

type ElevenLabsVoice = {
  voice_id: string;
  name: string;
  gender?: string | null;
  accent?: string | null;
  age?: string | null;
  description?: string | null;
  use_case?: string | null;
  preview_url?: string | null;
};

// 2026-10-01: a catalog entry built from one ElevenLabs workspace voice —
// name, gender, accent and description come from ElevenLabs' own labels so
// the numbered menu people see ("2. Arjun — Indian · male · calm") is
// filled in without typing.
function voiceFromElevenLabs(v: ElevenLabsVoice): VoiceEntry {
  const style = [v.age, v.use_case].filter(Boolean).join(', ').replace(/_/g, ' ');
  return {
    ...newVoice(),
    label: v.name || '',
    provider: 'elevenlabs',
    voice_id: v.voice_id,
    gender: (v.gender || '').toLowerCase(),
    accent: v.accent ? v.accent.charAt(0).toUpperCase() + v.accent.slice(1) : '',
    style,
    description: v.description || '',
  };
}

type VoiceOption = { id: string; label: string };
type LanguageOption = { code: string; label: string };

const GENDERS = [
  { value: 'female', label: 'Female' },
  { value: 'male', label: 'Male' },
  { value: 'neutral', label: 'Neutral' },
];

const NONE = '__none__';

// edge-tts ids look like "en-KE-ChilembaNeural"; anything else is an ElevenLabs voice id.
const EDGE_VOICE_RE = /^[a-z]{2,3}-[A-Za-z]{2,}(-[A-Za-z]+)?-\w+Neural$/;

function newVoice(): VoiceEntry {
  return {
    // Stable id saved on contacts who pick this voice — generated once,
    // never edited, so renaming a voice keeps everyone's choice.
    key: `voice_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    label: '',
    provider: 'elevenlabs',
    voice_id: '',
    gender: '',
    accent: '',
    languages: [],
    style: '',
    description: '',
    sample_text: {},
    enabled: true,
  };
}

function errorText(err: any, fallback: string): string {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d: any) => d.msg || JSON.stringify(d)).join(', ');
  return err?.message || fallback;
}

export default function CallingVoiceCatalogSettings() {
  const [selection, setSelection] = useState<VoiceSelection>({ calls_enabled: false, whatsapp_enabled: false, voices: [] });
  const [maxVoices, setMaxVoices] = useState(10);
  const [edgeVoices, setEdgeVoices] = useState<VoiceOption[]>([]);
  const [languages, setLanguages] = useState<LanguageOption[]>([]);
  const [elevenVoices, setElevenVoices] = useState<ElevenLabsVoice[] | null>(null);
  const [loadingEleven, setLoadingEleven] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Voices whose "More details" panel is open -- all collapsed by default.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [data, voiceData, languageData] = await Promise.all([
          ttsConfigAPI.getVoiceSelection(),
          ttsConfigAPI.getVoices().catch(() => ({})),
          ttsConfigAPI.getLanguages().catch(() => []),
        ]);
        if (cancelled) return;
        const doc = data?.voice_selection || {};
        setSelection({
          calls_enabled: !!doc.calls_enabled,
          whatsapp_enabled: !!doc.whatsapp_enabled,
          voices: Array.isArray(doc.voices) ? doc.voices : [],
        });
        setMaxVoices(data?.max_voices || 10);
        setEdgeVoices(Array.isArray(voiceData?.edge_tts) ? voiceData.edge_tts : []);
        setLanguages(Array.isArray(languageData) ? languageData : []);
      } catch (err: any) {
        if (!cancelled) setError(errorText(err, 'Failed to load voices'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      audioRef.current?.pause();
    };
  }, []);

  const updateVoice = (index: number, patch: Partial<VoiceEntry>) => {
    setSelection((s) => ({ ...s, voices: s.voices.map((v, i) => (i === index ? { ...v, ...patch } : v)) }));
  };

  const addVoice = () => setSelection((s) => ({ ...s, voices: [...s.voices, newVoice()] }));

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Provider follows the ID, so there's no provider field to fill in. Picking
  // one of your loaded ElevenLabs voices also fills empty details from it.
  const setVoiceId = (index: number, value: string) => {
    const id = value.trim();
    if ((elevenVoices || []).some((v) => v.voice_id === id)) {
      pickElevenVoice(index, id);
      return;
    }
    updateVoice(index, { voice_id: value, provider: EDGE_VOICE_RE.test(id) ? 'edge_tts' : 'elevenlabs' });
  };

  const removeVoice = (index: number) =>
    setSelection((s) => ({ ...s, voices: s.voices.filter((_, i) => i !== index) }));

  const toggleLanguage = (index: number, code: string, on: boolean) => {
    const current = selection.voices[index].languages || [];
    updateVoice(index, { languages: on ? [...current, code] : current.filter((c) => c !== code) });
  };

  const loadElevenLabsVoices = async () => {
    setLoadingEleven(true);
    try {
      const data = await ttsConfigAPI.getElevenLabsVoices();
      setElevenVoices(Array.isArray(data?.voices) ? data.voices : []);
    } catch (err: any) {
      toast({ title: "Couldn't load ElevenLabs voices", description: errorText(err, ''), variant: 'destructive' as any });
    } finally {
      setLoadingEleven(false);
    }
  };

  // Picking an ElevenLabs voice fills in empty fields from its own labels.
  const addedElevenIds = new Set(
    selection.voices.filter((v) => v.provider === 'elevenlabs').map((v) => v.voice_id)
  );
  const slotsLeft = Math.max(0, maxVoices - selection.voices.length);

  const addElevenVoice = (v: ElevenLabsVoice) => {
    if (addedElevenIds.has(v.voice_id) || slotsLeft <= 0) return;
    setSelection((s) => ({ ...s, voices: [...s.voices, voiceFromElevenLabs(v)] }));
  };

  // Fills every free slot (up to the limit) with voices not added yet, in
  // the order ElevenLabs lists them.
  const addFirstElevenVoices = () => {
    const toAdd = (elevenVoices || []).filter((v) => !addedElevenIds.has(v.voice_id)).slice(0, slotsLeft);
    if (!toAdd.length) return;
    setSelection((s) => ({ ...s, voices: [...s.voices, ...toAdd.map(voiceFromElevenLabs)] }));
    toast({ title: `Added ${toAdd.length} voice${toAdd.length === 1 ? '' : 's'}`, description: 'Check the names, then Save voices.' });
  };

  // ElevenLabs' own preview clip — a plain public URL, no API cost.
  const playPreview = (url?: string | null) => {
    if (!url) return;
    audioRef.current?.pause();
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.play().catch(() => undefined);
  };

  const pickElevenVoice = (index: number, voiceId: string) => {
    const picked = (elevenVoices || []).find((v) => v.voice_id === voiceId);
    const voice = selection.voices[index];
    updateVoice(index, {
      voice_id: voiceId,
      provider: 'elevenlabs',
      label: voice.label || picked?.name || '',
      gender: voice.gender || (picked?.gender || '').toLowerCase(),
      accent: voice.accent || picked?.accent || '',
      description: voice.description || picked?.description || '',
    });
  };

  const playSample = async (voice: VoiceEntry) => {
    if (!voice.voice_id) return;
    setPlayingKey(voice.key);
    try {
      const blob = await ttsConfigAPI.getVoiceSample({
        provider: voice.provider,
        voice_id: voice.voice_id,
        label: voice.label,
        text: voice.sample_text?.en || undefined,
      });
      audioRef.current?.pause();
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audioRef.current = audio;
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch (err: any) {
      let message = errorText(err, 'Sample failed');
      // A blob error body needs reading to show the server's message.
      if (err?.response?.data instanceof Blob) {
        try {
          const body = JSON.parse(await err.response.data.text());
          if (typeof body?.detail === 'string') message = body.detail;
        } catch {
          /* keep the generic message */
        }
      }
      toast({ title: 'Sample failed', description: message, variant: 'destructive' as any });
    } finally {
      setPlayingKey(null);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        calls_enabled: selection.calls_enabled,
        whatsapp_enabled: selection.whatsapp_enabled,
        voices: selection.voices.map((v) => ({
          ...v,
          label: v.label.trim(),
          voice_id: v.voice_id.trim(),
          accent: v.accent.trim(),
          style: v.style.trim(),
          description: v.description.trim(),
          sample_text: v.sample_text?.en?.trim() ? { en: v.sample_text.en.trim() } : {},
        })),
      };
      const data = await ttsConfigAPI.updateVoiceSelection(payload);
      const doc = data?.voice_selection || {};
      setSelection({
        calls_enabled: !!doc.calls_enabled,
        whatsapp_enabled: !!doc.whatsapp_enabled,
        voices: Array.isArray(doc.voices) ? doc.voices : [],
      });
      toast({ title: 'Saved', description: 'Voice choices updated.' });
    } catch (err: any) {
      const message = errorText(err, 'Failed to save voices');
      setError(message);
      toast({ title: 'Save failed', description: message, variant: 'destructive' as any });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-muted-foreground">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Loading voice choices…
      </div>
    );
  }

  return (
    <Card className="rounded-2xl border-border/60 shadow-sm">
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserRound className="h-4 w-4" />
            Voices people can choose
          </CardTitle>
          <CardDescription className="mt-1 max-w-xl">
            Up to {maxVoices} voices a contact can switch between by asking — on a call ("can you
            use a male voice?") or in WhatsApp chat ("I want to change the voice" → a numbered list,
            reply with a number). A choice made in either place is used on all their calls and
            voice notes. The Digital Companion keeps its own voice.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex items-start justify-between gap-4 rounded-xl border border-border/60 p-3">
            <div>
              <p className="text-sm font-medium">On WhatsApp calls</p>
              <p className="text-xs text-muted-foreground">Callers can ask during a call to hear and switch voices.</p>
            </div>
            <Switch
              checked={selection.calls_enabled}
              onCheckedChange={(on) => setSelection((s) => ({ ...s, calls_enabled: on }))}
            />
          </div>
          <div className="flex items-start justify-between gap-4 rounded-xl border border-border/60 p-3">
            <div>
              <p className="text-sm font-medium">In WhatsApp chat</p>
              <p className="text-xs text-muted-foreground">
                Users get a numbered voice list and reply with a number (or "hear 2" to listen).
              </p>
            </div>
            <Switch
              checked={selection.whatsapp_enabled}
              onCheckedChange={(on) => setSelection((s) => ({ ...s, whatsapp_enabled: on }))}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium">
            Voices <span className="text-muted-foreground">({selection.voices.length}/{maxVoices})</span>
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={loadElevenLabsVoices} disabled={loadingEleven} className="gap-1">
              {loadingEleven ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
              {elevenVoices ? 'Reload ElevenLabs voices' : 'Load my ElevenLabs voices'}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={addVoice} disabled={selection.voices.length >= maxVoices} className="gap-1">
              <Plus className="h-3.5 w-3.5" /> Add voice
            </Button>
          </div>
        </div>

        {elevenVoices && (
          <div className="space-y-2 rounded-xl border border-border/60 p-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm font-medium">
                Your ElevenLabs voices <span className="text-muted-foreground">({elevenVoices.length})</span>
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="gap-1"
                onClick={addFirstElevenVoices}
                disabled={slotsLeft === 0 || elevenVoices.every((v) => addedElevenIds.has(v.voice_id))}
              >
                <Plus className="h-3.5 w-3.5" /> Add first {Math.min(slotsLeft, elevenVoices.filter((v) => !addedElevenIds.has(v.voice_id)).length)}
              </Button>
            </div>
            {elevenVoices.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No voices in your ElevenLabs workspace yet. Add some in ElevenLabs (Voice Library → "Add to My
                Voices", e.g. African or Indian accents), then reload.
              </p>
            ) : (
              <div className="max-h-72 space-y-1 overflow-y-auto pr-1">
                {elevenVoices.map((v) => {
                  const added = addedElevenIds.has(v.voice_id);
                  const details = [v.accent, v.gender, v.age].filter(Boolean).join(' · ');
                  return (
                    <div key={v.voice_id} className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent/50">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 shrink-0 p-0"
                        onClick={() => playPreview(v.preview_url)}
                        disabled={!v.preview_url}
                        aria-label={`Preview ${v.name}`}
                      >
                        <Play className="h-3.5 w-3.5" />
                      </Button>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm">{v.name}</p>
                        {(details || v.description) && (
                          <p className="truncate text-xs text-muted-foreground">
                            {[details, v.description].filter(Boolean).join(' — ')}
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        size="sm"
                        variant={added ? 'ghost' : 'outline'}
                        className="h-7 shrink-0 gap-1"
                        onClick={() => addElevenVoice(v)}
                        disabled={added || slotsLeft === 0}
                      >
                        {added ? <><Check className="h-3.5 w-3.5" /> Added</> : <><Plus className="h-3.5 w-3.5" /> Add</>}
                      </Button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {selection.voices.length === 0 && (
          <p className="text-xs text-muted-foreground">
            No voices yet — add one, or load your ElevenLabs voices first to pick from them.
          </p>
        )}

        {/* 2026-10-01: one compact row per voice -- just Name + Voice ID (the
            provider is detected from the ID). Everything else is optional and
            sits under "More details", collapsed by default. */}
        <datalist id="voice-id-suggestions">
          {(elevenVoices || []).map((v) => (
            <option key={v.voice_id} value={v.voice_id}>
              {[v.name, v.accent, v.gender].filter(Boolean).join(' · ')}
            </option>
          ))}
          {edgeVoices.map((v) => (
            <option key={v.id} value={v.id}>{v.label}</option>
          ))}
        </datalist>

        <div className="space-y-2">
          {selection.voices.map((voice, index) => {
            const open = expanded.has(voice.key);
            const summary = [voice.accent, voice.gender, voice.style].filter(Boolean).join(' · ');
            return (
              <div key={voice.key} className="rounded-xl border border-border/60 bg-muted/10">
                <div className="flex flex-wrap items-center gap-2 p-2">
                  <span className="w-6 shrink-0 text-center text-xs font-medium text-muted-foreground">{index + 1}.</span>
                  <Input
                    className="h-8 w-40"
                    value={voice.label}
                    onChange={(e) => updateVoice(index, { label: e.target.value })}
                    placeholder="Name"
                  />
                  <Input
                    className="h-8 min-w-[12rem] flex-1 font-mono text-xs"
                    list="voice-id-suggestions"
                    value={voice.voice_id}
                    onChange={(e) => setVoiceId(index, e.target.value)}
                    placeholder="Voice ID (ElevenLabs ID or e.g. en-KE-ChilembaNeural)"
                  />
                  <span className="shrink-0 rounded bg-accent px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">
                    {voice.provider === 'edge_tts' ? 'Edge' : 'ElevenLabs'}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 gap-1"
                    onClick={() => playSample(voice)}
                    disabled={!voice.voice_id || playingKey !== null}
                  >
                    {playingKey === voice.key ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                    Sample
                  </Button>
                  <Switch
                    checked={voice.enabled}
                    onCheckedChange={(on) => updateVoice(index, { enabled: on })}
                    aria-label="Offered"
                    title="Offered to people"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1 text-xs text-muted-foreground"
                    onClick={() => toggleExpanded(voice.key)}
                    aria-expanded={open}
                  >
                    {open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                    More details
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    onClick={() => removeVoice(index)}
                    aria-label="Remove voice"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
                {!open && summary && (
                  <p className="-mt-1 px-10 pb-2 text-xs text-muted-foreground">{summary}</p>
                )}

                {open && (
                  <div className="space-y-3 border-t border-border/60 p-3">
                    <p className="text-xs text-muted-foreground">
                      Optional — shown in the numbered list people get (e.g. "2. Arjun — Indian · male · calm").
                    </p>
                    <div className="grid gap-2 sm:grid-cols-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Gender</Label>
                        <Select value={voice.gender || NONE} onValueChange={(v) => updateVoice(index, { gender: v === NONE ? '' : v })}>
                          <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={NONE}>Not set</SelectItem>
                            {GENDERS.map((g) => (
                              <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Accent</Label>
                        <Input className="h-8" value={voice.accent} onChange={(e) => updateVoice(index, { accent: e.target.value })} placeholder="e.g. Kenyan, Indian" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Style</Label>
                        <Input className="h-8" value={voice.style} onChange={(e) => updateVoice(index, { style: e.target.value })} placeholder="e.g. calm, warm" />
                      </div>
                      <div className="space-y-1 sm:col-span-3">
                        <Label className="text-xs text-muted-foreground">Sample line (English — translated automatically)</Label>
                        <Input
                          className="h-8"
                          value={voice.sample_text?.en || ''}
                          onChange={(e) => updateVoice(index, { sample_text: { ...(voice.sample_text || {}), en: e.target.value } })}
                          placeholder={`Default: "Hi, I'm ${voice.label || 'Amani'}. This is how I sound."`}
                        />
                      </div>
                    </div>
                    {languages.length > 0 && (
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">
                          Only offer to people using (none ticked = everyone)
                        </Label>
                        <div className="flex flex-wrap gap-4">
                          {languages.map((lang) => (
                            <label key={lang.code} className="flex items-center gap-1.5 text-sm">
                              <Checkbox
                                checked={(voice.languages || []).includes(lang.code)}
                                onCheckedChange={(on) => toggleLanguage(index, lang.code, !!on)}
                              />
                              {lang.label}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="flex justify-end border-t border-border/60 pt-4">
          <Button onClick={handleSave} disabled={saving} className="gap-1.5">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save voices
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
